import { useEffect, useRef, useState } from 'react'
import { Modal, TextInput, Textarea, NumberInput, Select, Group, Button, Box, Text, Loader } from '@mantine/core'
import { FiZap } from 'react-icons/fi'
import { IoFlash } from 'react-icons/io5'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import agent from '../../app/api/agent'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { TagPicker } from '../../app/common/ui'
import { FirearmPartFormValues } from '../../app/models/firearm'
import { Part, PartFormValues } from '../../app/models/part'
import { TagTargetType } from '../../app/models/tag'
import type { TagDto } from '../../app/models/tag'

// Category options grouped by platform for easier scanning
const CATEGORY_OPTIONS = [
  {
    group: 'AR-15 / General',
    items: [
      { value: 'Muzzle Device', label: 'Muzzle Device' },
      { value: 'Barrel', label: 'Barrel' },
      { value: 'Handguard / Rail', label: 'Handguard / Rail' },
      { value: 'Bolt Carrier Group', label: 'Bolt Carrier Group' },
      { value: 'Charging Handle', label: 'Charging Handle' },
      { value: 'Upper Receiver', label: 'Upper Receiver' },
      { value: 'Optic', label: 'Optic' },
      { value: 'Light / Laser', label: 'Light / Laser' },
      { value: 'Trigger Group', label: 'Trigger Group' },
      { value: 'Lower Receiver', label: 'Lower Receiver' },
      { value: 'Pistol Grip', label: 'Pistol Grip' },
      { value: 'Stock / Brace', label: 'Stock / Brace' },
      { value: 'Sling / Mount', label: 'Sling / Mount' }
    ]
  },
  {
    group: 'AK Platform',
    items: [
      { value: 'Handguard Upper', label: 'Handguard Upper' },
      { value: 'Handguard Lower', label: 'Handguard Lower' },
      { value: 'Gas Tube / Piston', label: 'Gas Tube / Piston' },
      { value: 'Dust Cover', label: 'Dust Cover' },
      { value: 'Side Rail Mount', label: 'Side Rail Mount' },
      { value: 'Magazine762', label: 'Magazine (7.62x39)' },
      { value: 'Magazine545', label: 'Magazine (5.45x39)' },
      { value: 'MagazineDrum', label: 'Magazine (Drum)' },
      { value: 'Magazine', label: 'Magazine (unspecified caliber)' },
      { value: 'Bayonet Lug', label: 'Bayonet Lug' }
    ]
  },
  {
    group: 'General',
    items: [
      { value: 'Other', label: 'Other' }
    ]
  }
]

// Flatten for Select component
const FLAT_CATEGORY_OPTIONS = [
  { value: '', label: 'Select category (optional)' },
  ...CATEGORY_OPTIONS.flatMap(g => g.items)
]

const CONDITION_OPTIONS = [
  { value: '', label: 'Select condition' },
  { value: 'New', label: 'New' },
  { value: 'Excellent', label: 'Excellent' },
  { value: 'Good', label: 'Good' },
  { value: 'Fair', label: 'Fair' },
  { value: 'Worn', label: 'Worn' },
  { value: 'Needs Replacement', label: 'Needs Replacement' }
]

const validationSchema = Yup.object({
  name: Yup.string().required('Part name is required').max(200),
  value: Yup.number().min(0, 'Value must be 0 or greater').required('Value is required')
})

interface Props {
  opened: boolean;
  onClose: () => void;
  onSave: (part: FirearmPartFormValues) => void;
  initialPart?: FirearmPartFormValues | null;
  loading?: boolean;
  /** When "standalone", part is saved to Parts Bin via API; hide Install date. */
  mode?: 'firearm' | 'standalone';
  /** Called when mode is "standalone" and user submits (create); then close and refresh bin. */
  onCreateStandalone?: (part: PartFormValues) => Promise<void>;
  /** When set with mode "standalone", form is pre-filled and submit calls onUpdateStandalone. */
  initialPartFromBin?: Part | null;
  /** Called when mode is "standalone" and editing existing part (initialPartFromBin set). */
  onUpdateStandalone?: (id: string, part: PartFormValues) => Promise<void>;
}

type FormValues = FirearmPartFormValues & { imageUrl?: string };

const emptyPart: FormValues = {
  name: '',
  description: '',
  partNumber: '',
  manufacturer: '',
  value: 0,
  condition: '',
  category: '',
  installDate: '',
  notes: '',
  imageUrl: ''
}

function partToFormValues (p: Part): FormValues {
  return {
    name: p.name ?? '',
    description: p.description ?? '',
    partNumber: p.partNumber ?? '',
    manufacturer: p.manufacturer ?? '',
    value: p.value ?? 0,
    condition: p.condition ?? '',
    category: p.category ?? '',
    installDate: '',
    notes: p.notes ?? '',
    imageUrl: p.imageUrl ?? ''
  }
}

export default function PartFormModal ({
  opened,
  onClose,
  onSave,
  initialPart,
  loading,
  mode = 'firearm',
  onCreateStandalone,
  initialPartFromBin,
  onUpdateStandalone
}: Props) {
  const isEdit = !!initialPart && mode === 'firearm'
  const isStandalone = mode === 'standalone'
  const isStandaloneEdit = isStandalone && !!initialPartFromBin
  const [partTags, setPartTags] = useState<TagDto[]>([])
  const [aiFilling, setAiFilling] = useState(false)
  const [aiError, setAiError] = useState<string | null>(null)
  const setFieldValueRef = useRef<((field: string, value: unknown) => void) | null>(null)
  useEffect(() => {
    if (initialPartFromBin?.tags) setPartTags(initialPartFromBin.tags)
    else setPartTags([])
  }, [initialPartFromBin?.tags, initialPartFromBin?.id])
  const initialValues: FormValues =
    isStandalone && initialPartFromBin
      ? partToFormValues(initialPartFromBin)
      : (initialPart ?? emptyPart) as FormValues

  const handleSubmit = async (values: FormValues) => {
    const partForm: PartFormValues = {
      name: values.name,
      description: values.description ?? '',
      partNumber: values.partNumber ?? '',
      manufacturer: values.manufacturer ?? '',
      value: values.value ?? 0,
      condition: values.condition ?? '',
      category: values.category ?? null,
      notes: values.notes ?? '',
      imageUrl: values.imageUrl ?? ''
    }
    if (isStandaloneEdit && onUpdateStandalone && initialPartFromBin) {
      await onUpdateStandalone(initialPartFromBin.id, partForm)
      onClose()
    } else if (isStandalone && onCreateStandalone) {
      await onCreateStandalone(partForm)
      onClose()
    } else {
      onSave(values)
      onClose()
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEdit || isStandaloneEdit ? 'Edit part' : 'Add part'}
      size="md"
      styles={{
        modal: { borderRadius: 12, padding: 24 },
        header: { marginBottom: 16 }
      }}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({ handleSubmit, values, setFieldValue, setFieldTouched, errors, touched, isValid }) => {
          // Keep a ref so the AI handler can call setFieldValue
          setFieldValueRef.current = setFieldValue

          const handleAiFill = async () => {
            if (!values.name.trim()) return
            setAiFilling(true)
            setAiError(null)
            try {
              const result = await agent.AiAssist.partFill(values.name.trim(), values.partNumber ?? '')
              if (result.description) setFieldValue('description', result.description)
              if (result.manufacturer) setFieldValue('manufacturer', result.manufacturer)
              if (result.msrpUsd != null) setFieldValue('value', result.msrpUsd)
            } catch {
              setAiError('AI fill failed. Try again.')
            } finally {
              setAiFilling(false)
            }
          }

          return (
          <Form onSubmit={handleSubmit}>
            {/* Part name + AI fill row */}
            <Box mb="md">
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <Text size="sm" weight={500}>
                  Part name <span style={{ color: '#f03e3e' }}>*</span>
                </Text>
                {isStandalone && !isStandaloneEdit && (
                  <Button
                    size="xs"
                    variant="subtle"
                    leftIcon={aiFilling ? <Loader size={11} /> : <FiZap size={11} />}
                    disabled={!values.name.trim() || aiFilling}
                    onClick={handleAiFill}
                    sx={{
                      color: '#E85D04',
                      fontFamily: 'DM Sans, sans-serif',
                      fontSize: 11,
                      fontWeight: 700,
                      padding: '2px 8px',
                      height: 24,
                      '&:hover': { background: 'rgba(232,93,4,0.1)' },
                      '&[data-disabled]': { color: 'var(--gs-muted)', opacity: 0.5 }
                    }}
                  >
                    {aiFilling ? 'Filling…' : 'AI Fill'}
                  </Button>
                )}
              </Box>
              <TextInput
                placeholder="e.g. Holosun 507C"
                value={values.name}
                onChange={(e) => setFieldValue('name', e.target.value)}
                onBlur={() => setFieldTouched('name', true)}
                error={touched.name && errors.name}
                radius="md"
              />
              {isStandalone && !isStandaloneEdit && !values.name.trim() && (
                <Text size="xs" color="dimmed" mt={4}>
                  Enter a part name to enable AI fill.
                </Text>
              )}
              {aiError && (
                <Text size="xs" color="red" mt={4}>
                  {aiError}
                </Text>
              )}
            </Box>

            <Textarea
              label="Description"
              placeholder="Optional description"
              value={values.description}
              onChange={(e) => setFieldValue('description', e.target.value)}
              minRows={2}
              mb="md"
              radius="md"
            />
            <Group grow mb="md">
              <TextInput
                label="Part number"
                placeholder="e.g. 507C-X2"
                value={values.partNumber}
                onChange={(e) => setFieldValue('partNumber', e.target.value)}
                mb={0}
                radius="md"
              />
              <TextInput
                label="Manufacturer"
                placeholder="e.g. Holosun"
                value={values.manufacturer}
                onChange={(e) => setFieldValue('manufacturer', e.target.value)}
                mb={0}
                radius="md"
              />
            </Group>
            <Group grow mb="md">
              <NumberInput
                label="Value ($)"
                placeholder="0.00"
                value={values.value || ''}
                onChange={(v) => setFieldValue('value', v ?? 0)}
                onBlur={() => setFieldTouched('value', true)}
                min={0}
                precision={2}
                decimalSeparator="."
                error={touched.value && errors.value}
                required
                mb={0}
                radius="md"
              />
              <Select
                label="Condition"
                data={CONDITION_OPTIONS}
                value={values.condition || null}
                onChange={(v) => setFieldValue('condition', v ?? '')}
                placeholder="Select condition"
                mb={0}
                radius="md"
              />
              <Select
                label="Category"
                data={FLAT_CATEGORY_OPTIONS}
                value={(values as FormValues).category || null}
                onChange={(v) => setFieldValue('category', v ?? null)}
                placeholder="Select category (optional)"
                mb={0}
                radius="md"
              />
            </Group>
            {!isStandalone && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500 }}>
                  Install date
                </label>
                <DatePicker
                  selected={values.installDate ? new Date(values.installDate) : null}
                  onChange={(date) => setFieldValue('installDate', date ? date.toISOString().split('T')[0] : '')}
                  dateFormat="yyyy-MM-dd"
                  placeholderText="Optional"
                  className="react-datepicker-wrapper"
                  style={{ width: '100%' }}
                />
              </div>
            )}
            {isStandalone && (
              <TextInput
                label="Image URL"
                placeholder="Optional image link"
                value={(values as FormValues).imageUrl ?? ''}
                onChange={(e) => setFieldValue('imageUrl', e.target.value)}
                mb="md"
                radius="md"
              />
            )}
            <Textarea
              label="Notes"
              placeholder="Optional notes"
              value={values.notes}
              onChange={(e) => setFieldValue('notes', e.target.value)}
              minRows={2}
              mb="md"
              radius="md"
            />
            {isStandaloneEdit && initialPartFromBin && (
              <div style={{ marginBottom: 16 }}>
                <TagPicker
                  targetType={TagTargetType.Part}
                  targetId={initialPartFromBin.id}
                  currentTags={partTags}
                  onTagApplied={(tag: TagDto) => setPartTags((prev) => [...prev, tag])}
                  onTagRemoved={(tag: TagDto) => setPartTags((prev) => prev.filter((t) => t.id !== tag.id))}
                  aiContext={{ name: values.name, description: values.description ?? undefined }}
                />
              </div>
            )}
            {isStandalone && !initialPartFromBin && (
              <p style={{ fontSize: '0.9rem', color: 'var(--mantine-color-dimmed)', marginBottom: 16 }}>
                Save the part first, then add tags.
              </p>
            )}
            {/* AmmoScore hint for new standalone parts */}
            {isStandalone && !isStandaloneEdit && (
              <Box
                sx={{
                  background: 'rgba(201,168,76,0.08)',
                  border: '1px solid rgba(201,168,76,0.25)',
                  borderRadius: 6,
                  padding: '8px 14px',
                  marginBottom: 12,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}
              >
                <IoFlash size={13} color="#C9A84C" />
                <Text sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12, color: 'var(--gs-muted)' }}>
                  Adding parts earns <strong style={{ color: '#C9A84C' }}>AmmoScore</strong> when the community votes them up.
                </Text>
              </Box>
            )}

            <Group position="right" mt="md">
              <Button variant="default" onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button type="submit" disabled={!isValid || loading} loading={loading}>
                {isEdit || isStandaloneEdit ? 'Save part' : 'Add part'}
              </Button>
            </Group>
          </Form>
          )
        }}
      </Formik>
    </Modal>
  )
}
